import type { CSSProperties, PropsWithChildren } from "react";
import { createContext, useContext, useMemo, useState } from "react";

import { cn } from "./lib/cn";

export type AiElementsTokens = {
  background: string;
  foreground: string;
  card: string;
  cardForeground: string;
  popover: string;
  popoverForeground: string;
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  muted: string;
  mutedForeground: string;
  accent: string;
  accentForeground: string;
  destructive: string;
  destructiveForeground: string;
  border: string;
  input: string;
  ring: string;
  radius: string;
  fontSans: string;
};

const ScopeContext = createContext<HTMLDivElement | null>(null);

/** Returns the scope element used as the container for Radix portals. */
export const useAiElementsScope = () => useContext(ScopeContext);

export type AiElementsScopeProps = PropsWithChildren<{
  tokens: AiElementsTokens;
  className?: string;
  /** Merged after the token variables, so a host can place the scope in its own stacking order. */
  style?: CSSProperties;
}>;

/** Isolates AI Elements styles and supplies theme tokens to its descendants. */
export const AiElementsScope = ({ children, className, style, tokens }: AiElementsScopeProps) => {
  const [scope, setScope] = useState<HTMLDivElement | null>(null);
  const scopeStyle = useMemo<CSSProperties & Record<`--${string}`, string>>(
    () => ({
      "--ml-ai-background": tokens.background,
      "--ml-ai-foreground": tokens.foreground,
      "--ml-ai-card": tokens.card,
      "--ml-ai-card-foreground": tokens.cardForeground,
      "--ml-ai-popover": tokens.popover,
      "--ml-ai-popover-foreground": tokens.popoverForeground,
      "--ml-ai-primary": tokens.primary,
      "--ml-ai-primary-foreground": tokens.primaryForeground,
      "--ml-ai-secondary": tokens.secondary,
      "--ml-ai-secondary-foreground": tokens.secondaryForeground,
      "--ml-ai-muted": tokens.muted,
      "--ml-ai-muted-foreground": tokens.mutedForeground,
      "--ml-ai-accent": tokens.accent,
      "--ml-ai-accent-foreground": tokens.accentForeground,
      "--ml-ai-destructive": tokens.destructive,
      "--ml-ai-destructive-foreground": tokens.destructiveForeground,
      "--ml-ai-border": tokens.border,
      "--ml-ai-input": tokens.input,
      "--ml-ai-ring": tokens.ring,
      "--ml-ai-radius": tokens.radius,
      "--ml-ai-font-sans": tokens.fontSans,
      ...style,
    }),
    [style, tokens],
  );

  return (
    <ScopeContext.Provider value={scope}>
      <div className={cn("ml-ai", className)} ref={setScope} style={scopeStyle}>
        {children}
      </div>
    </ScopeContext.Provider>
  );
};
